import { useState, useEffect, memo } from "react";
import { Search, X, Music } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { haptic } from "../utils/haptic";

// Sticky Search Header - Appears when scrolling past hero
const StickySearchHeader = memo(({ scrollThreshold = 400, eventContext = null }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const shouldShow = window.scrollY > scrollThreshold;
      if (shouldShow !== isVisible) {
        setIsVisible(shouldShow);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollThreshold, isVisible]);

  const handleSearchFocus = () => {
    haptic("light"); // 15ms haptic on focus
    setIsExpanded(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/events?search=${encodeURIComponent(searchQuery)}`);
      setIsExpanded(false);
    }
  };

  const handleClose = () => {
    setIsExpanded(false);
    setSearchQuery("");
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop when expanded */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9990]"
          onClick={handleClose}
        />
      )}

      {/* Sticky Header */}
      <div 
        className={`fixed top-0 left-0 right-0 z-[9995] transition-all duration-300 ${
          isVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
      >
        <div className="glass-strong border-b border-border/30 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              {/* Compact Logo */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-8 h-8 bg-primary flex items-center justify-center">
                  <span className="font-bold text-xs text-primary-foreground">XT</span>
                </div>
              </div>

              {/* Event Context (if on detail page) */}
              {eventContext && !isExpanded && (
                <div className="hidden sm:flex items-center gap-2 text-sm truncate flex-1">
                  <Music className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium truncate">{eventContext.name}</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground truncate">{eventContext.city}</span>
                </div>
              )}

              {/* Compact Search */}
              <div className={`transition-all duration-300 ${isExpanded ? "flex-1" : "flex-1 sm:flex-none"}`}>
                {isExpanded ? (
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search events, artists..."
                        className="w-full h-10 pl-10 pr-4 bg-background/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                        autoFocus
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="w-10 h-10 rounded-lg glass flex items-center justify-center"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </form>
                ) : (
                  <button
                    onClick={handleSearchFocus}
                    className="flex items-center gap-2 h-10 px-4 bg-background/30 border border-border/50 rounded-lg text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors w-full sm:w-auto"
                  >
                    <Search className="w-4 h-4" />
                    <span className="hidden sm:inline">Search</span>
                  </button>
                )}
              </div>

              {/* Quick Stats */}
              {!isExpanded && (
                <div className="hidden md:flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                    <span className="font-mono text-muted-foreground">LIVE</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

StickySearchHeader.displayName = "StickySearchHeader";

export default StickySearchHeader;
